// src/core/api/axiosConfig.ts
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../config/runtimeConfig';
import { getApiFailureMessage } from './apiFailureMessage';

// Prevent multiple 401 toasts and redirects when many requests fail at once (e.g. token expired)
let isRedirectingToLogin = false;
let lastConnectionErrorToast = 0;
const CONNECTION_ERROR_DEBOUNCE_MS = 3000;

/**
 * Generic model lookup failures are commonly produced by optional background
 * lookups (for example GTIN metadata after opening a saved remittance). The
 * calling component owns the context and may choose to ignore them, so the
 * global interceptor must not show a misleading toast.
 *
 * Contextual failures such as «حواله یافت نشد» are intentionally not matched.
 */
export function isGenericRecordNotFoundMessage(value: unknown): boolean {
  if (typeof value !== 'string') return false;

  const message = value.trim().replace(/[!！.]+$/u, '').trim();
  return /^(?:رکورد(?:ی| مورد نظر)? یافت نشد|record not found)$/iu.test(message);
}

export function getPersianValidationErrorMessage(data: any): string {
  const isPersianMessage = (message: unknown): message is string =>
    typeof message === 'string' && /[\u0600-\u06FF]/.test(message) && !/[a-z]/i.test(message);
  const validationMessages = data?.errors && typeof data.errors === 'object'
    ? Object.values(data.errors).flatMap((messages) => Array.isArray(messages) ? messages : [messages])
    : [];
  const localizedValidationMessage = validationMessages.find(isPersianMessage);
  if (localizedValidationMessage) return localizedValidationMessage;

  for (const candidate of [data?.error, data?.message]) {
    if (isPersianMessage(candidate)) return candidate;
  }

  return 'اطلاعات واردشده معتبر نیست؛ لطفاً موارد مشخص‌شده را بررسی کنید.';
}

function handle401Redirect(data: { message?: string; error?: string }) {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  const msg = data?.error || 'نشست شما منقضی شده است. لطفاً مجدداً وارد شوید.';
  toast.error(msg, { toastId: 'auth-expired' });
  localStorage.removeItem('token');
  // روی صفحات ورود فقط توکن پاک می‌شود و کاربر درجا می‌ماند
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isLoginPage = path === '/login_password' || path === '/Adminlogin_pass';
  if (isLoginPage) {
    return;
  }
  // در پنل مدیریت به ورود ادمین هدایت می‌شود
  if (path.startsWith('/admin')) {
    window.location.href = '/Adminlogin_pass';
    return;
  }
  window.location.href = '/login_password';
}

function handleConnectionError(message: string) {
  const now = Date.now();
  if (now - lastConnectionErrorToast < CONNECTION_ERROR_DEBOUNCE_MS) return;
  lastConnectionErrorToast = now;
  toast.error(message, { toastId: 'connection-error' });
}

// Prevent a flood of 403 toasts when several requests are denied at once (e.g. a financial
// manager lands on a dashboard that prefetches the حواله/بارنامه tabs they cannot access).
// All 403s within a short window collapse into a single toast, and the friendlier
// «دسترسی شما به بخش حواله و بارنامه ممکن نیست.» message wins when present.
let lastForbiddenToastAt = Number.NEGATIVE_INFINITY;
let lastForbiddenMessage: string | null = null;
const FORBIDDEN_TOAST_DEBOUNCE_MS = 3000;

export function handleForbidden(data: { message?: string; error?: string; errors?: unknown }) {
  const now = Date.now();
  const isFinancial = data?.errors === 'financial_section_denied';
  const msg = isFinancial
    ? data?.message || 'دسترسی شما به بخش حواله و بارنامه ممکن نیست.'
    : data?.message || data?.error || 'اجازه دسترسی به این بخش را ندارید';

  // Within the same burst, only upgrade the toast to the financial message (once).
  if (now - lastForbiddenToastAt < FORBIDDEN_TOAST_DEBOUNCE_MS) {
    if (isFinancial && lastForbiddenMessage !== msg) {
      lastForbiddenMessage = msg;
      toast.error(msg, { toastId: 'forbidden' });
    }
    return;
  }

  // First 403 of a new burst.
  lastForbiddenToastAt = now;
  lastForbiddenMessage = msg;
  toast.error(msg, { toastId: 'forbidden' });
}

function isAuthenticationRequest(url?: string) {
  if (!url) return false;
  return (
    url.includes('/v1/admin/login') ||
    url.includes('/v1/general/login') ||
    url.includes('/v1/token/reSendCode') ||
    url.includes('/v1/token/forgetPassword')
  );
}

function isDriverOtpRequest(url?: string): boolean {
  return /\/(?:drivers\/sendOtp|bill_loadings\/cancel\/send-otp)(?:[?#]|$)/i.test(url ?? '');
}

function isWaybillIssuanceRequest(url?: string): boolean {
  return /\/inquiry\/(insertBillLoading|prepareSign|checkBillLoadingSubmission|registerManualTrackingCode)(?:[?#]|$)/i.test(url ?? '');
}

function isWaybillCancellationRequest(url?: string): boolean {
  return /\/bill_loadings\/cancel(?:[?#]|$)/i.test(url ?? '');
}

function isIntroductionSubmission(url?: string): boolean {
  return /\/introduce\/insert(?:[?#]|$)/i.test(url ?? '');
}

export const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    // Do not leave the UI waiting until the browser or reverse proxy gives
    // up. A bounded timeout lets the caller show a retryable state instead of
    // turning a transient outage into an apparently stuck login/dashboard.
    timeout: 30000,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });

  // Retry only idempotent reads on transient transport/server failures. Never
  // replay login or financial mutations automatically.
  axiosRetry(instance, {
    retries: 3,
    retryDelay: (retryCount) => Math.min(retryCount * 1000, 3000),
    retryCondition: (error) => {
      const method = error.config?.method?.toLowerCase() ?? 'get';
      const isRead = ['get', 'head', 'options'].includes(method);
      const status = error.response?.status;
      const isTransientStatus = status === undefined || [408, 429, 502, 503, 504].includes(status);
      // Reconciliation is an expensive diagnostic query and is already
      // launched in the background by the accounting overview. Retrying it
      // automatically can create a request storm while PostgreSQL is busy.
      const url = error.config?.url ?? '';
      const isAccountingReconciliation = url.includes('/accounting/') && url.includes('reconcile');
      return isRead && isTransientStatus && !isAccountingReconciliation && !isDriverOtpRequest(url);
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      // Leave time for the bounded RMTO call; a client timeout must not invite a duplicate SMS request.
      if (isDriverOtpRequest(config.url)) config.timeout = 45000;
      if (isWaybillCancellationRequest(config.url)) config.timeout = 45000;
      // Outlast the backend's bounded RMTO call so its response can reach the form.
      if (isIntroductionSubmission(config.url)) config.timeout = 45000;
      // Allow the bounded RMTO request to finish before the browser gives up.
      if (isWaybillIssuanceRequest(config.url)) {
        config.timeout = /\/inquiry\/insertBillLoading(?:[?#]|$)/i.test(config.url ?? '') ? 150000 : 60000;
      }
      const token = localStorage.getItem('token');
      if (token && !isAuthenticationRequest(config.url)) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // برای FormData، هدر Content-Type رو حذف کن تا مرورگر خودش تنظیم کنه
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error) => {
      const originalRequest = error.config;

      // AbortController is used throughout the dashboard/accounting panels to
      // cancel stale requests during workspace changes and unmounts. A cancel
      // is expected control flow, not a connectivity failure, so do not show
      // a misleading global "connection error" toast for it.
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }
      // Retried GET failures pass through this interceptor more than once.
      // Keep one actionable notification for the final error.
      if (error.__notificationShown) return Promise.reject(error);
      error.__notificationShown = true;
      const failureMessage = getApiFailureMessage(error);
      error.message = failureMessage;

      // Handle offline status (manual retry after reconnect)
      if (!window.navigator.onLine && originalRequest && !isDriverOtpRequest(originalRequest.url)
          && !isWaybillIssuanceRequest(originalRequest.url) && !isWaybillCancellationRequest(originalRequest.url)
          && !isIntroductionSubmission(originalRequest.url)) {
        originalRequest._retry = true;
        setTimeout(() => {
          if (originalRequest && window.navigator.onLine) {
            instance(originalRequest);
          }
        }, 5000);
        handleConnectionError(failureMessage);
        return Promise.reject(error);
      }

      // Handle different error status codes
      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 401:
            // Invalid credentials must stay on the login page so Login.tsx can
            // show the server error instead of triggering a redirect/refresh.
            if (!isAuthenticationRequest(originalRequest?.url)) {
              handle401Redirect(data || {});
            }
            break;

          case 400:
          case 404:
            if (data?.error && !isGenericRecordNotFoundMessage(data.error)) {
              toast.error(data.error);
            }
            if (data?.errors && typeof data.errors === 'object') {
              Object.entries(data.errors).forEach(([key, value]) => {
                if (key !== 'otp' && Array.isArray(value) && value.length > 0) {
                  toast.error(value[0] as string);
                }
              });
            }
            break;

          case 422:
            toast.error(getPersianValidationErrorMessage(data));
            break;

          case 403:
            handleForbidden(data || {});
            break;

          case 409:
            // The number-management page presents contextual conflicts itself.
            if (!/\/waybill_number\//.test(originalRequest?.url ?? '')) toast.error(failureMessage);
            break;
          case 500:
          case 408:
          case 429:
          case 502:
          case 503:
          case 504:
            toast.error(failureMessage);
            break;

          default:
            toast.error(failureMessage);
        }
      } else if (isIntroductionSubmission(originalRequest?.url)) {
        toast.error(failureMessage, { toastId: 'introduction-unconfirmed' });
      } else {
        handleConnectionError(failureMessage);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const axiosInstance = createAxiosInstance();
